import { baseApi } from "../api";
import { GoalStatusFilter } from "../types";
import {
  CreateSavingsGoalPayload,
  SavingsGoalResponse,
  UpdateSavingsGoalPayload,
} from "./types";

interface UpdateSavingsGoalArgs {
  id: number;
  payload: UpdateSavingsGoalPayload;
}

const savingsGoalsListTags = (result?: SavingsGoalResponse[]) => [
  { type: "SavingsGoal" as const, id: "LIST" },
  ...(result?.map((goal) => ({
    type: "SavingsGoal" as const,
    id: goal.id,
  })) ?? []),
];

const invalidateSavingsGoal = (id: number) => [
  { type: "SavingsGoal" as const, id },
  { type: "SavingsGoal" as const, id: "LIST" },
];

const invalidateDeletedSavingsGoal = (id: number) => [
  ...invalidateSavingsGoal(id),
  "HealthTargets" as const,
];

const invalidateCreatedSavingsGoal = (payload: CreateSavingsGoalPayload) => [
  { type: "SavingsGoal" as const, id: "LIST" },
  ...(payload.previousGoalId === undefined
    ? []
    : [
        { type: "SavingsGoal" as const, id: payload.previousGoalId },
        "HealthTargets" as const,
      ]),
];

export const savingsGoalsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSavingsGoals: builder.query<
      SavingsGoalResponse[],
      GoalStatusFilter | void
    >({
      query: (status) => ({
        url: "/savings-goals",
        params: status ? { status } : undefined,
      }),
      providesTags: savingsGoalsListTags,
    }),

    getSavingsGoal: builder.query<SavingsGoalResponse, number>({
      query: (id) => ({ url: "/savings-goals/" + id }),
      providesTags: (_result, _error, id) => [{ type: "SavingsGoal", id }],
    }),

    createSavingsGoal: builder.mutation<unknown, CreateSavingsGoalPayload>({
      query: (payload) => ({
        url: "/savings-goals",
        method: "post",
        data: payload,
      }),
      invalidatesTags: (_result, _error, payload) =>
        invalidateCreatedSavingsGoal(payload),
    }),

    updateSavingsGoal: builder.mutation<void, UpdateSavingsGoalArgs>({
      query: ({ id, payload }) => ({
        url: "/savings-goals/" + id,
        method: "patch",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) => invalidateSavingsGoal(id),
    }),

    deleteSavingsGoal: builder.mutation<void, number>({
      query: (id) => ({
        url: "/savings-goals/" + id,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, id) =>
        invalidateDeletedSavingsGoal(id),
    }),
  }),
});

export const {
  useGetSavingsGoalsQuery,
  useGetSavingsGoalQuery,
  useCreateSavingsGoalMutation,
  useUpdateSavingsGoalMutation,
  useDeleteSavingsGoalMutation,
} = savingsGoalsApi;
